import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiBell,
  FiCheckCircle,
  FiInfo,
  FiLink,
  FiRefreshCw,
  FiSend,
  FiTrash2,
} from "react-icons/fi";
import { WSNotification } from "../../../services/remote/notification/WSNotification";
import { Notification, NotificationType } from "../../../entity/Notification";
import { COLORS } from "../../../utils/Colors";

const EMPTY_PAYLOAD: Notification = {
  title: "",
  message: "",
  type: NotificationType.INFO,
  isRequired: false,
  url: "",
};

/** Appends a hex alpha to a 6-digit hex color, e.g. tint("#4361ee") -> light wash. */
const tint = (hex: string, alpha = "1A") => `${hex}${alpha}`;

const TYPE_META: Record<
  NotificationType,
  { label: string; color: string; icon: React.ElementType }
> = {
  [NotificationType.INFO]: {
    label: "Info",
    color: COLORS.Status.Info,
    icon: FiInfo,
  },
  [NotificationType.SUCCESS]: {
    label: "Success",
    color: COLORS.Status.Success,
    icon: FiCheckCircle,
  },
  [NotificationType.WARNING]: {
    label: "Warning",
    color: COLORS.Palette.DarkYellow,
    icon: FiAlertTriangle,
  },
  [NotificationType.ERROR]: {
    label: "Error",
    color: COLORS.Status.Error,
    icon: FiAlertCircle,
  },
  [NotificationType.UPDATE]: {
    label: "Update",
    color: COLORS.Brand.Accent,
    icon: FiRefreshCw,
  },
};

const cardProps = {
  bg: COLORS.Background.Primary,
  border: "1px solid",
  borderColor: COLORS.Background.Tertiary,
  borderRadius: "xl",
} as const;

const inputFocus = {
  focusBorderColor: COLORS.Brand.Primary,
  borderColor: COLORS.Background.Tertiary,
  borderRadius: "md",
} as const;

/* -------------------------------------------------------------------------- */
/*  Live preview: shows the person sending what the store user will see        */
/* -------------------------------------------------------------------------- */
const NotificationPreview = ({ data }: { data: Notification }) => {
  const meta = TYPE_META[data.type];
  const Icon = meta.icon;
  const hasContent = data.title.trim() || data.message.trim();

  return (
    <Box
      borderRadius="lg"
      border="1px solid"
      borderColor={COLORS.Background.Tertiary}
      borderLeft="4px solid"
      borderLeftColor={meta.color}
      bg={COLORS.Neutral.LightGray}
      p={4}
      aria-live="polite"
    >
      <HStack align="start" spacing={3}>
        <Flex
          flexShrink={0}
          w={8}
          h={8}
          align="center"
          justify="center"
          borderRadius="full"
          bg={tint(meta.color, "26")}
          color={meta.color}
        >
          <Icon size={16} />
        </Flex>
        <Box minW={0} flex="1">
          <Text
            fontWeight="700"
            fontSize="sm"
            color={data.title.trim() ? COLORS.Text.Primary : COLORS.Text.Muted}
            wordBreak="break-word"
          >
            {data.title.trim() || "Your title appears here"}
          </Text>
          <Text
            mt={1}
            fontSize="sm"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            color={
              data.message.trim() ? COLORS.Neutral.DarkGray : COLORS.Text.Muted
            }
          >
            {data.message.trim() || "Your message appears here."}
          </Text>
          {data.url?.trim() && (
            <HStack mt={2} spacing={1} color={COLORS.Brand.Primary}>
              <FiLink size={12} />
              <Text fontSize="xs" noOfLines={1}>
                {data.url.trim()}
              </Text>
            </HStack>
          )}
          <HStack mt={3} spacing={2}>
            <Button
              size="xs"
              bg={data.isRequired ? COLORS.Brand.Primary : "transparent"}
              color={
                data.isRequired ? COLORS.Text.Inverse : COLORS.Neutral.Gray
              }
              border={data.isRequired ? "none" : "1px solid"}
              borderColor={COLORS.Background.Tertiary}
              pointerEvents="none"
              tabIndex={-1}
            >
              {data.isRequired ? "Acknowledge" : "Dismiss"}
            </Button>
          </HStack>
        </Box>
      </HStack>
      {!hasContent && (
        <Text mt={3} fontSize="xs" color={COLORS.Neutral.Gray}>
          Start typing to see how store users will receive this.
        </Text>
      )}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main component                                                             */
/* -------------------------------------------------------------------------- */
export const NotificationManager = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [payload, setPayload] = useState<Notification>(EMPTY_PAYLOAD);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Notification | null>(null);
  const [filter, setFilter] = useState<"all" | "required">("all");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const requiredCount = useMemo(
    () => notifications.filter((n) => n.isRequired).length,
    [notifications],
  );

  const visible = useMemo(
    () =>
      filter === "required"
        ? notifications.filter((n) => n.isRequired)
        : notifications,
    [notifications, filter],
  );

  const fetchNotifications = async () => {
    setLoadingList(true);
    try {
      const result = await WSNotification.getInstance().getAllNotifications();
      setNotifications(result ?? []);
    } catch (error) {
      console.error(error);
      toast({
        title: "Couldn't load notifications",
        description: "Check your connection, then refresh the list.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!payload.title.trim()) next.title = "Add a title";
    if (!payload.message.trim()) next.message = "Add a message";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreateNotification = async () => {
    if (!validate()) return;
    setCreating(true);
    try {
      await WSNotification.getInstance().createNotifications({
        ...payload,
        url: payload.url?.trim() || undefined,
      });
      toast({
        title: "Notification sent",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setPayload(EMPTY_PAYLOAD);
      fetchNotifications();
    } catch (error) {
      console.error(error);
      toast({
        title: "Couldn't send notification",
        description: "Check your connection, then send it again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = (notification: Notification) => {
    setPendingDelete(notification);
    onOpen();
  };

  const handleDelete = async () => {
    if (!pendingDelete?.id) return;
    const id = pendingDelete.id;
    setDeletingId(id);
    try {
      await WSNotification.getInstance().deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      toast({
        title: "Notification deleted",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Couldn't delete notification",
        description: "Check your connection, then delete it again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
      onClose();
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1180px" mx="auto">
      {/* Header */}
      <Flex align="center" gap={3} mb={1}>
        <Flex
          w={10}
          h={10}
          align="center"
          justify="center"
          borderRadius="lg"
          bg={COLORS.Transparent.BrandPrimary}
          color={COLORS.Brand.Primary}
        >
          <FiBell size={20} />
        </Flex>
        <Heading size="lg" color={COLORS.Text.Primary}>
          Notification manager
        </Heading>
      </Flex>
      <Text color={COLORS.Neutral.Gray} mb={8} ml={{ md: 13 }}>
        Send announcements to store users and review what's gone out.
      </Text>

      <Flex gap={6} direction={{ base: "column", lg: "row" }} align="start">
        {/* ------------------------------ Compose ------------------------------ */}
        <Box
          {...cardProps}
          flex="1"
          w="full"
          minW={{ lg: "380px" }}
          maxW={{ lg: "420px" }}
          p={6}
          position={{ lg: "sticky" }}
          top={{ lg: 4 }}
        >
          <Heading size="sm" mb={5} color={COLORS.Text.Primary}>
            New notification
          </Heading>

          <VStack spacing={5} align="stretch">
            <FormControl isInvalid={!!errors.title} isRequired>
              <FormLabel fontSize="sm" color={COLORS.Text.Dark}>
                Title
              </FormLabel>
              <Input
                {...inputFocus}
                value={payload.title}
                onChange={(e) => {
                  setPayload((p) => ({ ...p, title: e.target.value }));
                  setErrors((prev) => ({ ...prev, title: "" }));
                }}
                placeholder="e.g. Scheduled maintenance tonight"
              />
              <FormErrorMessage>{errors.title}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.message} isRequired>
              <FormLabel fontSize="sm" color={COLORS.Text.Dark}>
                Message
              </FormLabel>
              <Textarea
                {...inputFocus}
                value={payload.message}
                onChange={(e) => {
                  setPayload((p) => ({ ...p, message: e.target.value }));
                  setErrors((prev) => ({ ...prev, message: "" }));
                }}
                placeholder="Write what store users need to know"
                rows={4}
                resize="vertical"
              />
              <FormErrorMessage>{errors.message}</FormErrorMessage>
            </FormControl>

            {/* Type picker */}
            <FormControl as="fieldset">
              <FormLabel as="legend" fontSize="sm" color={COLORS.Text.Dark}>
                Type
              </FormLabel>
              <Flex gap={2} wrap="wrap" role="radiogroup" aria-label="Type">
                {Object.values(NotificationType).map((type) => {
                  const meta = TYPE_META[type];
                  const Icon = meta.icon;
                  const selected = payload.type === type;
                  return (
                    <Box
                      key={type}
                      as="button"
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setPayload((p) => ({ ...p, type }))}
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      px={3}
                      py={1.5}
                      fontSize="sm"
                      fontWeight={selected ? "600" : "500"}
                      borderRadius="full"
                      border="1px solid"
                      borderColor={
                        selected ? meta.color : COLORS.Background.Tertiary
                      }
                      bg={selected ? tint(meta.color) : "transparent"}
                      color={
                        selected ? COLORS.Text.Primary : COLORS.Neutral.Gray
                      }
                      transition="background 0.15s, border-color 0.15s"
                      _hover={{ borderColor: meta.color }}
                      _focusVisible={{
                        outline: "2px solid",
                        outlineColor: COLORS.Brand.Primary,
                        outlineOffset: "2px",
                      }}
                    >
                      <Box as="span" color={meta.color} display="flex">
                        <Icon size={14} />
                      </Box>
                      {meta.label}
                    </Box>
                  );
                })}
              </Flex>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={COLORS.Text.Dark}>
                Link (optional)
              </FormLabel>
              <Input
                {...inputFocus}
                value={payload.url ?? ""}
                onChange={(e) =>
                  setPayload((p) => ({ ...p, url: e.target.value }))
                }
                placeholder="/orders/123 or https://..."
              />
            </FormControl>

            <FormControl>
              <Flex align="center" justify="space-between">
                <FormLabel
                  htmlFor="require-ack"
                  fontSize="sm"
                  mb={0}
                  color={COLORS.Text.Dark}
                >
                  Require acknowledgment
                </FormLabel>
                <Switch
                  id="require-ack"
                  isChecked={payload.isRequired}
                  onChange={(e) =>
                    setPayload((p) => ({ ...p, isRequired: e.target.checked }))
                  }
                  sx={{
                    ".chakra-switch__track[data-checked]": {
                      bg: COLORS.Brand.Primary,
                    },
                    ".chakra-switch__track:focus-visible, .chakra-switch__track[data-focus]":
                      {
                        boxShadow: `0 0 0 3px ${COLORS.Brand.Primary}66`,
                      },
                  }}
                />
              </Flex>
              <FormHelperText fontSize="xs" color={COLORS.Neutral.Gray}>
                Required notifications block the user's screen until they
                respond. Optional ones show as a popup they can dismiss.
              </FormHelperText>
            </FormControl>

            {/* Live preview */}
            <Box>
              <Text
                fontSize="sm"
                fontWeight="500"
                mb={2}
                color={COLORS.Text.Dark}
              >
                Preview
              </Text>
              <NotificationPreview data={payload} />
            </Box>

            <Button
              leftIcon={<FiSend />}
              bg={COLORS.Brand.Primary}
              color={COLORS.Text.Inverse}
              _hover={{ bg: COLORS.Interaction.Hover }}
              _active={{ bg: COLORS.Brand.Secondary }}
              _focusVisible={{
                boxShadow: `0 0 0 3px ${COLORS.Brand.Primary}66`,
              }}
              onClick={handleCreateNotification}
              isLoading={creating}
              loadingText="Sending"
            >
              Send notification
            </Button>
          </VStack>
        </Box>

        {/* -------------------------------- List ------------------------------- */}
        <Box {...cardProps} flex="2" w="full" overflow="hidden">
          <Flex
            align="center"
            justify="space-between"
            wrap="wrap"
            gap={3}
            px={6}
            py={4}
            borderBottom="1px solid"
            borderColor={COLORS.Background.Tertiary}
          >
            <Box>
              <Heading size="sm" color={COLORS.Text.Primary}>
                Sent notifications
              </Heading>
              {!loadingList && notifications.length > 0 && (
                <Text fontSize="xs" color={COLORS.Neutral.Gray} mt={0.5}>
                  {notifications.length} sent, {requiredCount} require
                  acknowledgment
                </Text>
              )}
            </Box>

            <HStack spacing={2}>
              <ButtonGroup size="sm" isAttached variant="outline">
                {(["all", "required"] as const).map((key) => {
                  const active = filter === key;
                  return (
                    <Button
                      key={key}
                      onClick={() => setFilter(key)}
                      aria-pressed={active}
                      borderColor={COLORS.Background.Tertiary}
                      bg={active ? COLORS.Brand.Primary : "transparent"}
                      color={
                        active ? COLORS.Text.Inverse : COLORS.Neutral.DarkGray
                      }
                      _hover={{
                        bg: active
                          ? COLORS.Interaction.Hover
                          : COLORS.Neutral.LightGray,
                      }}
                    >
                      {key === "all" ? "All" : "Required"}
                    </Button>
                  );
                })}
              </ButtonGroup>
              <Tooltip label="Refresh list" hasArrow>
                <IconButton
                  aria-label="Refresh list"
                  icon={<FiRefreshCw />}
                  size="sm"
                  variant="ghost"
                  color={COLORS.Neutral.Gray}
                  onClick={fetchNotifications}
                  isLoading={loadingList}
                />
              </Tooltip>
            </HStack>
          </Flex>

          {loadingList ? (
            <Flex justify="center" align="center" py={16}>
              <Spinner color={COLORS.Brand.Primary} />
            </Flex>
          ) : visible.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              textAlign="center"
              px={6}
              py={16}
              color={COLORS.Neutral.Gray}
            >
              <Flex
                w={12}
                h={12}
                align="center"
                justify="center"
                borderRadius="full"
                bg={COLORS.Background.Secondary}
                color={COLORS.Text.Muted}
              >
                <FiBell size={22} />
              </Flex>
              <Text mt={4} fontWeight="600" color={COLORS.Text.Primary}>
                {filter === "required"
                  ? "No required notifications"
                  : "No notifications sent yet"}
              </Text>
              <Text mt={1} fontSize="sm">
                {filter === "required"
                  ? "Turn on “Require acknowledgment” when you send one."
                  : "Write your first announcement in the form."}
              </Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead bg={COLORS.Neutral.LightGray}>
                  <Tr>
                    <Th>Type</Th>
                    <Th>Notification</Th>
                    <Th>Required</Th>
                    <Th>Link</Th>
                    <Th textAlign="right">
                      <Box as="span" srOnly>
                        Actions
                      </Box>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visible.map((n) => {
                    const meta = TYPE_META[n.type];
                    const Icon = meta.icon;
                    return (
                      <Tr key={n.id} _hover={{ bg: COLORS.Neutral.LightGray }}>
                        <Td verticalAlign="top" py={4}>
                          <Badge
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={1}
                            borderRadius="md"
                            textTransform="none"
                            fontWeight="600"
                            bg={tint(meta.color)}
                            color={COLORS.Text.Primary}
                          >
                            <Box as="span" color={meta.color} display="flex">
                              <Icon size={12} />
                            </Box>
                            {meta.label}
                          </Badge>
                        </Td>
                        <Td verticalAlign="top" py={4} maxW="320px">
                          <Text
                            fontWeight="600"
                            color={COLORS.Text.Primary}
                            noOfLines={1}
                          >
                            {n.title}
                          </Text>
                          <Text
                            mt={0.5}
                            fontSize="sm"
                            color={COLORS.Neutral.Gray}
                            whiteSpace="normal"
                            noOfLines={2}
                          >
                            {n.message}
                          </Text>
                        </Td>
                        <Td verticalAlign="top" py={4}>
                          {n.isRequired ? (
                            <Badge
                              textTransform="none"
                              borderRadius="md"
                              px={2}
                              py={1}
                              bg={COLORS.Transparent.BrandPrimary}
                              color={COLORS.Brand.Primary}
                            >
                              Required
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color={COLORS.Text.Muted}>
                              Optional
                            </Text>
                          )}
                        </Td>
                        <Td verticalAlign="top" py={4}>
                          {n.url ? (
                            <Text
                              fontSize="xs"
                              color={COLORS.Brand.Primary}
                              maxW="140px"
                              noOfLines={1}
                              title={n.url}
                            >
                              {n.url}
                            </Text>
                          ) : (
                            <Text fontSize="xs" color={COLORS.Text.Muted}>
                              None
                            </Text>
                          )}
                        </Td>
                        <Td verticalAlign="top" py={3} textAlign="right">
                          <Tooltip label="Delete notification" hasArrow>
                            <IconButton
                              aria-label={`Delete notification: ${n.title}`}
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              color={COLORS.Status.Error}
                              _hover={{ bg: COLORS.Palette.LightRed }}
                              isLoading={deletingId === n.id}
                              onClick={() => confirmDelete(n)}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Flex>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Delete notification
            </AlertDialogHeader>
            <AlertDialogBody color={COLORS.Neutral.DarkGray}>
              {pendingDelete && (
                <Text fontWeight="600" mb={2} noOfLines={2}>
                  {pendingDelete.title}
                </Text>
              )}
              This removes it for every user who hasn't seen it yet. You can't
              undo this.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="ghost"
                onClick={onClose}
                isDisabled={!!deletingId}
              >
                Cancel
              </Button>
              <Button
                ml={3}
                bg={COLORS.Status.Error}
                color={COLORS.Text.Inverse}
                _hover={{ bg: COLORS.Palette.Danger }}
                onClick={handleDelete}
                isLoading={!!deletingId}
              >
                Delete notification
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
